/**
 * freelanceCorridors.js
 * Definitions and data for 12 freelance rate currency corridor calculators.
 */

export const CORRIDORS = [
  {
    slug: 'usd-to-inr',
    from: 'USD',
    to: 'INR',
    fromSymbol: '$',
    toSymbol: '₹',
    title: 'USD to INR Freelance Rate Calculator',
    marketContext: 'India is the largest global market for freelance software development, design, and IT services billing US clients in USD.',
    defaultHourly: 40,
    purchasingPowerContext: '',
  },
  {
    slug: 'usd-to-php',
    from: 'USD',
    to: 'PHP',
    fromSymbol: '$',
    toSymbol: '₱',
    title: 'USD to PHP Freelance Rate Calculator',
    marketContext: 'The Philippines is the leading destination for virtual assistants, digital marketing, customer support, and design contractors serving US clients.',
    defaultHourly: 25,
    purchasingPowerContext: '',
  },
  {
    slug: 'usd-to-pkr',
    from: 'USD',
    to: 'PKR',
    fromSymbol: '$',
    toSymbol: 'Rs',
    title: 'USD to PKR Freelance Rate Calculator',
    marketContext: 'Pakistan is one of the fastest-growing freelance markets for software engineering, web development, and digital services billing US clients in USD.',
    defaultHourly: 30,
    purchasingPowerContext: '',
  },
  {
    slug: 'usd-to-eur',
    from: 'USD',
    to: 'EUR',
    fromSymbol: '$',
    toSymbol: '€',
    title: 'USD to EUR Freelance Rate Calculator',
    marketContext: 'European contractors billing US tech startups and enterprises in USD require clear conversion strategies to manage tax and living expenses in Euros.',
    defaultHourly: 75,
    purchasingPowerContext: '',
  },
  {
    slug: 'usd-to-gbp',
    from: 'USD',
    to: 'GBP',
    fromSymbol: '$',
    toSymbol: '£',
    title: 'USD to GBP Freelance Rate Calculator',
    marketContext: 'UK contractors and boutique consultancies billing US clients in USD must account for GBP conversion margins, IR35 considerations, and UK corporate tax.',
    defaultHourly: 85,
    purchasingPowerContext: '',
  },
  {
    slug: 'usd-to-brl',
    from: 'USD',
    to: 'BRL',
    fromSymbol: '$',
    toSymbol: 'R$',
    title: 'USD to BRL Freelance Rate Calculator',
    marketContext: 'Brazil is a primary nearshore tech talent hub for US companies seeking senior software engineers in overlapping time zones.',
    defaultHourly: 50,
    purchasingPowerContext: '',
  },
  {
    slug: 'usd-to-mxn',
    from: 'USD',
    to: 'MXN',
    fromSymbol: '$',
    toSymbol: '$',
    title: 'USD to MXN Freelance Rate Calculator',
    marketContext: 'Mexico offers optimal nearshore alignment with US Central/Mountain time zones for software development, design, and engineering roles.',
    defaultHourly: 45,
    purchasingPowerContext: '',
  },
  {
    slug: 'usd-to-ngn',
    from: 'USD',
    to: 'NGN',
    fromSymbol: '$',
    toSymbol: '₦',
    title: 'USD to NGN Freelance Rate Calculator',
    marketContext: 'Nigeria has a rapidly expanding ecosystem of software developers, product designers, and technical writers earning USD from global remote employers.',
    defaultHourly: 35,
    purchasingPowerContext: '',
  },
  {
    slug: 'usd-to-cad',
    from: 'USD',
    to: 'CAD',
    fromSymbol: '$',
    toSymbol: 'C$',
    title: 'USD to CAD Freelance Rate Calculator',
    marketContext: 'Canadian freelancers frequently work with US clients due to geographic proximity, shared business hours, and favorable USD-CAD conversion rates.',
    defaultHourly: 70,
    purchasingPowerContext: '',
  },
  {
    slug: 'eur-to-inr',
    from: 'EUR',
    to: 'INR',
    fromSymbol: '€',
    toSymbol: '₹',
    title: 'EUR to INR Freelance Rate Calculator',
    marketContext: 'European enterprises in Germany, Netherlands, and France frequently outsource software engineering and digital services to Indian tech professionals.',
    defaultHourly: 45,
    purchasingPowerContext: '',
  },
  {
    slug: 'gbp-to-inr',
    from: 'GBP',
    to: 'INR',
    fromSymbol: '£',
    toSymbol: '₹',
    title: 'GBP to INR Freelance Rate Calculator',
    marketContext: 'The UK-India business corridor is one of the oldest outsourcing channels for software development, accounting, and legal process outsourcing.',
    defaultHourly: 50,
    purchasingPowerContext: '',
  },
  {
    slug: 'usd-to-vnd',
    from: 'USD',
    to: 'VND',
    fromSymbol: '$',
    toSymbol: '₫',
    title: 'USD to VND Freelance Rate Calculator',
    marketContext: 'Vietnam is a major Southeast Asian hub for mobile app development, game design, and software engineering teams billing in US Dollars.',
    defaultHourly: 30,
    purchasingPowerContext: '',
  },
];

// Fallback rates if API is un-cached or offline
export const CORRIDOR_APPROX_RATES = {
  'usd-to-inr': 86.50,
  'usd-to-php': 58.20,
  'usd-to-pkr': 278.50,
  'usd-to-eur': 0.92,
  'usd-to-gbp': 0.79,
  'usd-to-brl': 5.65,
  'usd-to-mxn': 20.40,
  'usd-to-ngn': 1520.00,
  'usd-to-cad': 1.40,
  'eur-to-inr': 94.00,
  'gbp-to-inr': 109.50,
  'usd-to-vnd': 25400.00,
};

// Payment Rail Fees Matrix per corridor (percentage off mid-market + fixed fee)
export const PAYMENT_RAILS = [
  { name: 'Wise (TransferWise)', feePercent: 0.45, fixedFeeUSD: 0.50, speed: 'Same day / Next day', note: 'Uses true mid-market rate with transparent low fee' },
  { name: 'Payoneer', feePercent: 2.00, fixedFeeUSD: 1.50, speed: '1–2 business days', note: 'Standard for marketplace platforms, includes FX spread' },
  { name: 'PayPal / Xoom', feePercent: 3.75, fixedFeeUSD: 0.30, speed: 'Instant to 1 day', note: 'High conversion markup plus cross-border payment fee' },
  { name: 'Direct SWIFT Wire', feePercent: 3.50, fixedFeeUSD: 25.00, speed: '2–4 business days', note: 'Receiving bank FX margin + intermediary correspondent fees' },
];

export function getCorridor(slug) {
  return CORRIDORS.find(c => c.slug === slug) ?? null;
}
